import React, { Suspense, useEffect, useState } from 'react'
import { Await, defer, Link, useLoaderData } from 'react-router-dom'
import { education, citizenshipEducation } from '../util/api'
import { Title } from './helper/DocumentTitle'
import LoadingSpinner from './components/LoadingSpinner'
import Video from './helper/Video'
import styles from '../styles/module/CitizenshipEducation.module.scss'

/* Use defer to load categories and videos in parallel */
export const loader = async () => {
  return defer({
    categories: education(),
    videos: citizenshipEducation(),
  })
}

export default function CitizenshipEducation() {
  const loaderData = useLoaderData()
  const [selectedCategoryId, setSelectedCategoryId] = useState(0) // 0 represents "All"
  const [activeVideo, setActiveVideo] = useState(null)

  const pageName = 'آموزش شهروندی'
  Title(pageName)

  /* Set header text on mount */
  useEffect(() => {
    const headerSpan = document.querySelector('header span')
    if (headerSpan) headerSpan.innerText = pageName
  }, [])

  /* Handle selecting a video from the list */
  const handleVideoSelect = (video) => {
    setActiveVideo(video)
    /* Smooth scroll to top so the player is visible on mobile */
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <section className={styles.citizenshipEducation}>
      <div className="__frame" data-width="xlarge">
        <div className={styles.topBar}>
          <Link to="../" className="btn btn-back-top">
            بازگشت
          </Link>
          <h2 className={styles.title}>{pageName}</h2>
        </div>

        {/* Filter Tabs */}
        <Suspense fallback={<LoadingSpinner />}>
          <Await resolve={loaderData.categories}>
            {(categories) => (
              <ul className={styles.categoryList}>
                <li className={selectedCategoryId === 0 ? styles.activeTab : ''} onClick={() => setSelectedCategoryId(0)}>
                  همه
                </li>
                {categories.map((cat) => (
                  <li key={cat.id} className={selectedCategoryId === cat.id ? styles.activeTab : ''} onClick={() => setSelectedCategoryId(cat.id)}>
                    {cat.name} ({cat.citizenshipEducationVideoCount || 0})
                  </li>
                ))}
              </ul>
            )}
          </Await>
        </Suspense>

        <div className={styles.mainLayout}>
          {/* Text-Based Playlist Column */}
          <div className={styles.playlistColumn}>
            <div className="card">
              <div className="card__header">لیست پخش ویدیوها</div>
              <div className={styles.scrollableList}>
                <Suspense fallback={<LoadingSpinner />}>
                  <Await resolve={loaderData.videos}>
                    {(videos) => {
                      /* Filter videos based on selected category id from education table */
                      const filteredVideos = selectedCategoryId === 0 ? videos : videos.filter((v) => v.education_id === selectedCategoryId)

                      return filteredVideos.map((item) => (
                        <div key={item.id} className={`${styles.videoListItem} ${activeVideo?.id === item.id ? styles.activeItem : ''}`} onClick={() => handleVideoSelect(item)}>
                          <span className={styles.playIcon}>●</span>
                          <div className={styles.itemContent}>
                            <span className={styles.videoTitle}>{item.title}</span>
                            <span className={styles.videoCategory}># {item.category_name || 'آموزش شهروندی'}</span>
                          </div>
                        </div>
                      ))
                    }}
                  </Await>
                </Suspense>
              </div>
            </div>
          </div>
          {/* Main Player Column */}
          <div className={styles.playerColumn}>
            <Suspense fallback={<LoadingSpinner />}>
              <Await resolve={loaderData.videos}>
                {(videos) => {
                  /* Default to the first video in the list if none is selected */
                  const currentVideo = activeVideo || videos[0]
                  if (!currentVideo) return <div className={styles.empty}>ویدیویی یافت نشد.</div>

                  return (
                    <div className={styles.stickyVideo}>
                      <Video
                        key={currentVideo.id} // Essential: forces player to reload when source changes
                        options={{
                          autoplay: !!activeVideo,
                          controls: true,
                          responsive: true,
                          fluid: true,
                          poster: `${process.env.REACT_APP_UPLOAD}images/${currentVideo.poster}`,
                          sources: [
                            {
                              src: `${process.env.REACT_APP_UPLOAD}video/${currentVideo.url}`,
                              type: 'video/webm',
                            },
                          ],
                        }}
                      />
                      <div className={styles.videoDetails}>
                        <h3>{currentVideo.title}</h3>
                        <p>{currentVideo.description || 'بدون توضیح مربی'}</p>
                      </div>
                    </div>
                  )
                }}
              </Await>
            </Suspense>
          </div>
        </div>
      </div>
    </section>
  )
}
